import { firestore, database, functions } from '@breakroom-nx/apis';
import { createApi, fakeBaseQuery } from '@reduxjs/toolkit/query/react';

import {
  ref,
  set,
  onValue,
  query as dbQuery,
  orderByChild,
  push,
  get,
  limitToFirst,
  limitToLast,
  endBefore,
} from 'firebase/database';

import { IMessage } from 'react-native-gifted-chat';
import { getUserData, Flair, User, Room } from '@breakroom-nx/classes';
import { RoomMember, IMessageExt } from '@breakroom-nx/helpers';
import { httpsCallable } from 'firebase/functions';

export const roomAPI = createApi({
  reducerPath: 'roomApi',
  baseQuery: fakeBaseQuery(),
  // keepUnusedDataFor: 3600,
  tagTypes: ['Message', 'Member'],
  endpoints: (builder) => ({
    // Get Messages for Room from Realtime Database
    getMessagesForRoom: builder.query<IMessageExt[], any>({
      providesTags: (result, error, arg) =>
        result
          ? [
              ...result.map(({ _id }) => ({ type: 'Message' as const, _id })),
              'Message',
            ]
          : ['Message'],
      async queryFn({
        roomId,
        limit = 5,
        before,
      }: {
        roomId: string;
        limit?: number;
        before?: string;
      }) {
        return {
          data: await new Promise((resolve, reject) => {
            const messagesRef = ref(database, 'messages/' + roomId);

            let messageQuery;
            if (before) {
              messageQuery = dbQuery(
                messagesRef,
                orderByChild('createdAt'),
                endBefore(before),
                limitToLast(limit)
              );
            } else {
              messageQuery = dbQuery(
                messagesRef,
                orderByChild('createdAt'),
                limitToLast(limit)
              );
            }

            onValue(
              messageQuery,
              (snapshot) => {
                let messages: IMessageExt[] = [];
                snapshot.forEach((doc) => {
                  const val = doc.val();
                  // console.log(`VAL LANG: ${val.lang}`);
                  const message: IMessageExt = {
                    _id: val._id,
                    text: val.text,
                    createdAt: val.createdAt,
                    user: val.user,
                    system: val.system,
                    translations: val.translations,
                    lang: val.lang,
                  };
                  messages.push(message);
                });
                // Sort by createdAt descending for GiftedChat
                messages.sort(
                  (a, b) =>
                    new Date(b.createdAt).getTime() -
                    new Date(a.createdAt).getTime()
                );
                resolve(messages);
              },
              { onlyOnce: true }
            );
          }),
        };
      },
      async onCacheEntryAdded(
        arg,
        { updateCachedData, cacheDataLoaded, cacheEntryRemoved }
      ) {
        // Only set up real-time for the latest page (no before)
        if (!arg.before) {
          const { roomId, limit = 5 } = arg;
          const messagesRef = ref(database, 'messages/' + roomId);
          const messageQuery = dbQuery(
            messagesRef,
            orderByChild('createdAt'),
            limitToLast(limit)
          );
          let unsubscribe = () => {};
          try {
            await cacheDataLoaded;
            unsubscribe = onValue(messageQuery, (snapshot) => {
              let messages: IMessageExt[] = [];
              snapshot.forEach((doc) => {
                const val = doc.val();
                const message: IMessageExt = {
                  _id: val._id,
                  text: val.text,
                  createdAt: val.createdAt,
                  user: val.user,
                  system: val.system,
                  translations: val.translations,
                  lang: val.lang,
                };
                messages.push(message);
              });
              messages.sort(
                (a, b) =>
                  new Date(b.createdAt).getTime() -
                  new Date(a.createdAt).getTime()
              );
              updateCachedData(() => messages);
            });
          } catch {}
          await cacheEntryRemoved;
          unsubscribe && unsubscribe();
        }
      },
    }),

    // Get Last Message for Room from Realtime Database
    getLastMessageForRoom: builder.query<IMessageExt[], string>({
      providesTags: (result, error, arg) => [{ type: 'Message', arg }],

      async queryFn(roomId) {
        return {
          data: await new Promise((resolve, reject) => {
            const messagesRef = ref(database, 'messages/' + roomId);
            const messageQuery = dbQuery(
              messagesRef,
              orderByChild('createdAt'),
              limitToLast(1)
            );

            // probably more logic here to get your final shape
            onValue(messageQuery, (snapshot) => {
              let messages: IMessageExt[] = [];
              snapshot.forEach((doc) => {
                // console.log(`Message Retrieved: ${doc.val().text}`);
                const message = {
                  _id: doc.val()._id,
                  text: doc.val().text,
                  createdAt: doc.val().createdAt,
                  user: doc.val().user,
                  system: doc.val().system,
                } as IMessageExt;
                messages.push(message);
              });
              // console.log(`Resolving Last Message: ${messages}`);
              resolve(messages);
            });
          }),
        };
      },
      async onCacheEntryAdded(
        roomId,
        { updateCachedData, cacheDataLoaded, cacheEntryRemoved }
      ) {
        let unsubscribe = () => {};
        try {
          await cacheDataLoaded.then((cache) => {
            // console.log(
            //   `Cache Data Loaded: [Messages Size: ${cache.data.length}]`
            // );
          });

          const messagesRef = ref(database, 'messages/' + roomId);
          const messageQuery = dbQuery(
            messagesRef,
            orderByChild('createdAt'),
            limitToLast(1)
          );

          unsubscribe = onValue(messageQuery, (snapshot) => {
            let messages: IMessageExt[] = [];
            snapshot.forEach((doc) => {
              // console.log(`Message Retrieved: ${doc.val().text}`);
              const message = {
                _id: doc.val()._id,
                text: doc.val().text,
                createdAt: doc.val().createdAt,
                user: doc.val().user,
                system: doc.val().system,
              } as IMessageExt;

              messages.push(message);
            });

            updateCachedData((draft) => {
              return messages;
            });
          });
        } catch (err) {
          // console.log(`[USER API] (Error) Unable to update cache for user`);
        }

        await cacheEntryRemoved;
        unsubscribe && unsubscribe();
        return unsubscribe;
      },
    }),

    // Get Last Messages for Rooms from Realtime Database
    getLastMessagesForRooms: builder.query<Room[], Room[]>({
      // The input is an array of Room objects (with at least id)
      async queryFn(rooms) {
        // Initial data: just return the rooms as-is (lastMessage may be stale)
        return { data: rooms };
      },
      async onCacheEntryAdded(
        rooms,
        { updateCachedData, cacheDataLoaded, cacheEntryRemoved }
      ) {
        let unsubscribes: (() => void)[] = [];
        try {
          await cacheDataLoaded;

          // Listen to lastMessage for each room
          rooms.forEach((room) => {
            const lastMsgRef = ref(database, `chats/${room.id}/lastMessage`);
            const unsubscribe = onValue(lastMsgRef, (snapshot) => {
              if (snapshot.exists()) {
                const lastMessage = snapshot.val();
                updateCachedData((draft) => {
                  // Update the room's lastMessage
                  const idx = draft.findIndex((r) => r.id === room.id);
                  if (idx !== -1) {
                    draft[idx] = {
                      ...draft[idx],
                      lastMessage,
                      streakDuration: draft[idx].streakDuration || 0,
                      timeLeft: draft[idx].timeLeft || 0,
                    };
                  }
                  // Sort by lastMessage.createdAt descending
                  draft.sort(
                    (a, b) =>
                      new Date(b.lastMessage?.createdAt || 0).getTime() -
                      new Date(a.lastMessage?.createdAt || 0).getTime()
                  );
                });
              }
            });
            unsubscribes.push(() => unsubscribe());
          });
        } catch (err) {
          // console.log(
          //   `[ROOM API] (Error) Unable to update last messages for rooms`
          // );
        }

        await cacheEntryRemoved;
        unsubscribes.forEach((unsub) => unsub());
      },
    }),

    // Get Members for Room From Realtime Database
    getMembersForRoom: builder.query<RoomMember[] | undefined, Room>({
      async queryFn(room) {
        // console.info(`[ROOM API]: Getting members for room: ${room.id}`);
        return {
          data: await new Promise((resolve, reject) => {
            const membersRef = ref(database, 'members/' + room.id);
            onValue(membersRef, (snapshot) => {
              let members: RoomMember[] = [];
              snapshot.forEach((doc) => {
                // console.log(`Member Retrieved: ${doc.val().username}`);
                const flair = new Flair(doc.val().flair);
                const member = {
                  uid: doc.key,
                  flair: flair,
                  isHost: doc.val().isHost,
                  username: doc.val().username,
                  avatar: doc.val().avatar,
                  geoCode: doc.val().geoCode,
                } as RoomMember;
                // console.log(
                //   `Resolving Members: ${members} | UID: ${member.uid}`
                // );
                members.push(member);
              });
              // console.log(`Resolving Members: ${members}`);
              resolve(members);
            });
          }),
        };
        // try {
        //   console.info(`[ROOM API]: Getting members for room: ${room.id}`);

        //   const members: RM[] | undefined = room.members;
        //   let roomMembers: RoomMember[] = [];

        //   const membersData = members?.map(async (member) => {
        //     const user = await getUserData(member.uid);
        //     const flair = new Flair(member.flair);

        //     return new RoomMember(user as User, flair);
        //   });

        //   if (membersData) {
        //     roomMembers = await Promise.all(membersData);
        //   }

        //   console.log(`[ROOM API] Room member count: ${roomMembers.length}`);
        //   return { data: roomMembers };
        // } catch (err) {
        //   console.log(`[ROOM API] (ERROR) ${err}`);
        //   return { error: err };
        // }
      },
      async onCacheEntryAdded(
        room,
        { updateCachedData, cacheDataLoaded, cacheEntryRemoved }
      ) {
        let unsubscribe = () => {};
        try {
          await cacheDataLoaded.then((cache) => {
            // console.log(
            //   `Cache Data Loaded: [Members Size: ${cache.data.length}]`
            // );
          });

          const membersRef = ref(database, 'members/' + room.id);
          unsubscribe = onValue(membersRef, (snapshot) => {
            let members: RoomMember[] = [];
            snapshot.forEach((doc) => {
              // console.log(`Member Retrieved: ${doc.val().username}`);
              const flair = new Flair(doc.val().flair);
              const member = {
                uid: doc.key,
                flair: flair,
                isHost: doc.val().isHost,
                username: doc.val().username,
                // avatar: doc.val().avatar,
                // geoCode: doc.val().geoCode,
              } as RoomMember;
              members.push(member);
            });

            updateCachedData((draft) => {
              return members;
            });
          });
        } catch (err) {
          // console.log(
          //   `[ROOM API] (Error) Unable to update cache for room members`
          // );
        }

        await cacheEntryRemoved;
        unsubscribe && unsubscribe();
        return unsubscribe;
      },
    }),

    //! Mutations
    sendMessage: builder.mutation<
      null,
      { user: User | undefined; messages: IMessageExt[]; roomId: string }
    >({
      invalidatesTags: ['Message'],
      async queryFn({ user, messages, roomId }) {
        try {
          // console.log(`[ROOM API] Sending a message`);
          if (!user || !messages || !roomId) {
            return { error: 'No user, message or room found' };
          }

          // console.log(`[ROOM API] Sending new message to room ${roomId}`);
          // console.log(`Sending message: ${messages[0].text}`);
          const messagesRef = ref(database, 'messages/' + roomId);
          const lastMessageRef = ref(database, `chats/${roomId}/lastMessage`);
          const createdAt = messages[0].createdAt as Date;

          // Push the message to the messages list
          push(messagesRef, {
            _id: messages[0]._id,
            text: messages[0].text,
            createdAt: createdAt.toISOString(),
            user: {
              _id: messages[0].user._id,
              name: messages[0].user.name,
              geoCode: user.geoCode,
              avatar: user.avatar,
              theme: user.theme,
            },
            system: 0,
          });

          // Set the lastMessage metadata for the room
          await set(lastMessageRef, {
            _id: messages[0]._id,
            text: messages[0].text,
            createdAt: createdAt.toISOString(),
            user: {
              _id: messages[0].user._id,
              name: messages[0].user.name,
              geoCode: user.geoCode,
              avatar: user.avatar,
              theme: user.theme,
            },
            system: 0,
          });

          return { data: null };
        } catch (err) {
          // console.log(
          //   `[ROOM API] Error Sending a Message to the Room. Error ${err}`
          // );
          return { error: err };
        }
      },
    }),
  }),
});

export const {
  useGetMessagesForRoomQuery,
  useLazyGetMessagesForRoomQuery,
  useGetLastMessageForRoomQuery,
  useGetLastMessagesForRoomsQuery,
  useGetMembersForRoomQuery,
  useSendMessageMutation,
} = roomAPI;
